import { create } from 'zustand'
import { api } from './api'
import { queryClient } from './queryClient'
import { useFilial } from './filial'
import { useCanal } from './canal'

export interface Usuario {
  id: number; nome: string; email: string; perfil: string;
  ativo: boolean; empresa_id: number | null; avatar_url?: string;
  // RBAC: nivel = base de segurança; permissoes = {rota: bool} controla o menu
  nivel?: string; cargo_nome?: string; permissoes?: Record<string, boolean> | null;
}
export interface EmpresaInfo {
  id: number; nome_fantasia: string; plano: string; modulos_ativos: Record<string, boolean> | null;
  max_usuarios: number; max_produtos: number; max_pedidos_mes: number;
  // FEATURE 01/07: rotas escondidas do menu globalmente (aba Configurações > Menu)
  menu_oculto?: string[];
}
interface AuthState {
  token: string | null; user: Usuario | null; empresa: EmpresaInfo | null;
  login: (email: string, senha: string) => Promise<void>;
  loginById: (usuarioId: number, senha: string) => Promise<void>;
  logout: () => void; loadFromStorage: () => void;
  refreshEmpresa: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

// Hidratação síncrona do estado inicial a partir do localStorage
// Evita que o guard RequireAuth redirecione pra /login no primeiro render após F5
function getInitialState(): Pick<AuthState, 'token' | 'user' | 'empresa'> {
  try {
    const token = localStorage.getItem('pain_token')
    const u = localStorage.getItem('pain_user')
    const e = localStorage.getItem('pain_empresa')
    if (token && u) {
      return {
        token,
        user: JSON.parse(u),
        empresa: e ? JSON.parse(e) : null,
      }
    }
  } catch {}
  return { token: null, user: null, empresa: null }
}

export const useAuth = create<AuthState>((set) => ({
  ...getInitialState(),  // <<< AQUI: carrega IMEDIATAMENTE, sem esperar useEffect
  login: async (email, senha) => {
    const form = new URLSearchParams()
    form.append('username', email); form.append('password', senha)
    const { data } = await api.post('/auth/login', form, {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    })
    localStorage.setItem('pain_token', data.access_token)
    localStorage.setItem('pain_user', JSON.stringify(data.usuario))
    if (data.empresa) localStorage.setItem('pain_empresa', JSON.stringify(data.empresa))
    // Login limpo: nunca herda impersonação/só-leitura de uma sessão anterior
    // (senão um pain_imp grudado deixava o admin preso em "modo visualização").
    localStorage.removeItem('pain_imp')
    localStorage.removeItem('pain_impersonator')
    localStorage.removeItem('velore_readonly')
    // BUG: o login novo não zerava a store de filial. Se um ADMIN tinha ativado
    // "Todas as filiais" (pain_modo_consolidado=true) e em seguida alguém logava
    // como OPERADOR sem clicar em Sair (troca de conta / sessão expirada), o modo
    // consolidado VAZAVA: o operador entrava sem filial definida e o PDV abria a
    // caixa de seleção de filial como se fosse admin. FIX: resetar a filial no
    // login (mesma lógica do reset de impersonação acima) — sessão sempre limpa.
    useFilial.getState().resetFilial()
    useCanal.getState().resetCanal()   // canal de PDV não vaza entre contas (09/07)
    set({ token: data.access_token, user: data.usuario, empresa: data.empresa })
  },
  loginById: async (usuarioId, senha) => {
    // Login escolhendo o usuário no dropdown (por id) + senha.
    const { data } = await api.post('/auth/login-id', { usuario_id: usuarioId, senha })
    localStorage.setItem('pain_token', data.access_token)
    localStorage.setItem('pain_user', JSON.stringify(data.usuario))
    if (data.empresa) localStorage.setItem('pain_empresa', JSON.stringify(data.empresa))
    localStorage.removeItem('pain_imp')
    localStorage.removeItem('pain_impersonator')
    localStorage.removeItem('velore_readonly')
    // Ver login() acima: reseta a filial pra o modo consolidado de um admin
    // anterior não vazar pro operador que loga em seguida.
    useFilial.getState().resetFilial()
    useCanal.getState().resetCanal()   // canal de PDV não vaza entre contas (09/07)
    set({ token: data.access_token, user: data.usuario, empresa: data.empresa })
  },
  logout: () => {
    localStorage.removeItem('pain_token')
    localStorage.removeItem('pain_user')
    localStorage.removeItem('pain_empresa')
    localStorage.removeItem('pain_imp')
    localStorage.removeItem('pain_impersonator')
    localStorage.removeItem('velore_readonly')
    // BUG: logout era SPA (navigate('/login'), sem reload), então o cache em
    // memória do React Query e a store de filial sobreviviam. Ao logar em outra
    // conta a logo/nome da filial anterior vazavam (queryKey ['filial-logo', id]
    // colide entre tenants; nome herdado de filialAtual). FIX: zerar a filial e
    // limpar TODO o cache de query no logout, garantindo sessão limpa.
    useFilial.getState().resetFilial()
    useCanal.getState().resetCanal()   // canal de PDV não vaza entre contas (09/07)
    queryClient.clear()
    set({ token: null, user: null, empresa: null })
  },
  loadFromStorage: () => {
    // Mantido para compatibilidade — força reload do storage
    set(getInitialState())
  },
  refreshUser: async () => {
    // Sincroniza o usuário logado com o backend (traz nivel/permissoes RBAC
    // sem exigir relogin). Silencioso em caso de falha (offline/token).
    try {
      const { data } = await api.get('/auth/me')
      if (data?.usuario) {
        localStorage.setItem('pain_user', JSON.stringify(data.usuario))
        const patch: any = { user: data.usuario }
        if (data.empresa) {
          localStorage.setItem('pain_empresa', JSON.stringify(data.empresa))
          patch.empresa = data.empresa
        }
        set(patch)
      }
    } catch {}
  },
  refreshEmpresa: async () => {
    try {
      const { data } = await api.get('/empresa/atual')
      const empresaInfo = {
        id: data.id, nome_fantasia: data.nome_fantasia, plano: data.plano,
        modulos_ativos: data.modulos_ativos || {},
        max_usuarios: data.max_usuarios || 0, max_produtos: data.max_produtos || 0,
        max_pedidos_mes: data.max_pedidos_mes || 0,
        menu_oculto: data.menu_oculto || [],  // FEATURE 01/07: itens do menu escondidos
      }
      localStorage.setItem('pain_empresa', JSON.stringify(empresaInfo))
      set({ empresa: empresaInfo })
    } catch {}
  },
}))

export function cn(...classes: (string | boolean | undefined | null)[]): string {
  return classes.filter(Boolean).join(' ')
}
